import { useSignal } from '@preact/signals';

import type { PictoContext } from '@amumin/api-client';
import type { PictoCategory } from '@amumin/ui-core';
import { IconPicker } from '@amumin/ui';
import { Icon, Pictogram, resolvePicto } from '@amumin/ui-core';

import { pickerHidden, usePictoCatalog } from '../lib/pictoCatalog.js';
import { appText, messages } from '../state/session.js';

/**
 * Pictogramveld: een grote tegel met het gekozen pictogram en zijn naam; tikken klapt de
 * IconPicker uit (zoeken in het Nederlands, filter per categorie). Geen lijst met id's meer.
 * 0024: `context` bepaalt wat uit het zicht staat (docs/DESIGN.md §8.P.5): 'general' alleen
 * geloofsopties, 'food' ook eetvlaggen (boodschappen, voorraad, recepten). De teller komt van de
 * server (badge); "Alles tonen" en zoeken vinden alles terug, met de reden.
 */
export function PictoField({
  id,
  label,
  value,
  onChange,
  groups,
  fallbackLabel,
  context = 'general',
}: {
  id: string;
  label: string;
  value: string | null;
  onChange: (icon: string | null) => void;
  groups?: ReadonlyArray<PictoCategory>;
  /** Tekst voor de letter-fallback als er (nog) geen pictogram is, bv. de titel. */
  fallbackLabel?: string;
  context?: PictoContext;
}) {
  const m = messages.value;
  const a = appText.value;
  const open = useSignal(false);
  const catalog = usePictoCatalog(context);
  const picto = resolvePicto(value);
  const name = picto ? ((m.design.picto as Record<string, string>)[picto] ?? picto) : null;
  return (
    <div class="picto-field" data-name={id} data-context={context}>
      <span class="field-label" id={`${id}-label`}>
        {label}
      </span>
      <button
        type="button"
        class="picto-field-button"
        aria-expanded={open.value ? 'true' : 'false'}
        aria-labelledby={`${id}-label ${id}-value`}
        onClick={() => (open.value = !open.value)}
      >
        <span class="picto-field-tile">
          {picto ? (
            <Pictogram icon={picto} label={name ?? ''} size={40} decorative />
          ) : (
            <Icon id="image" size={24} />
          )}
        </span>
        <span class="picto-field-text" id={`${id}-value`}>
          {name ?? (fallbackLabel ? a.editor.pictoChoose : m.design.iconPicker.none)}
        </span>
        <Icon
          id={open.value ? 'chevron-up' : 'chevron-down'}
          size={20}
          class="picto-field-chevron"
        />
      </button>
      {open.value ? (
        <div class="picto-field-picker">
          <IconPicker
            name={`${id}-picker`}
            value={picto}
            onChange={(next) => {
              onChange(next);
              open.value = false;
            }}
            {...(groups ? { groups } : {})}
            hidden={pickerHidden(catalog)}
            labels={{
              label: m.design.iconPicker.label,
              search: m.design.iconPicker.search,
              none: m.design.iconPicker.none,
              noResults: m.design.iconPicker.noResults,
              all: m.design.iconPicker.all,
              picto: m.design.picto,
              keywords: m.design.pictoKeywords,
              category: m.design.pictoCategory,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
